import { useEffect, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { api } from "../services/api";
import { Link } from "react-router-dom";

const AdminPurchases = () => {
  const [purchases, setPurchases] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const limit = 20;
  const [searchQuery, setSearchQuery] = useState("");

  const fetchData = async (pageToLoad = 1, status = "", query = "") => {
    const params = new URLSearchParams({
      page: pageToLoad,
      limit,
      ...(status && { status }),
      ...(query && { q: query })
    }).toString();

    const res = await api.get(`/api/admin/purchases/list?${params}`);
    setPurchases(res.data.purchases);
    setPage(res.data.page);
    setTotal(res.data.total);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const totalPages = Math.ceil(total / limit);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const sortedPurchases = [...purchases].sort((a, b) => {
    const aVal = a[sortBy] ?? "";
    const bVal = b[sortBy] ?? "";

    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    }

    const aStr = aVal.toString().toLowerCase();
    const bStr = bVal.toString().toLowerCase();

    if (aStr < bStr) return sortDir === "asc" ? -1 : 1;
    if (aStr > bStr) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <Box sx={{
      backgroundImage: 'url("/assets/bg-pitchest.jpg")',
      backgroundSize: "cover",
      minHeight: "100vh",
      px: 2,
      py: 6,
    }}>
      <Box
        sx={{
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          borderRadius: 4,
          padding: { xs: 2, md: 4 },
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h5">💳 Purchases</Typography>
          <Link to="/admin" style={{ color: "#90caf9", fontWeight: "bold" }}>⬅ Back</Link>
        </Box>

        <Typography variant="body2" sx={{ mb: 2 }}>
          Total: {total} — Paid: {purchases.filter(p => p.payment_status === "paid").length} — Page {page} of {totalPages}
        </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <Box>
          <label style={{ marginRight: 8 }}>Search:</label>
          <input
            type="text"
            placeholder="Search by email"
            value={searchQuery}
            style={{
              padding: "6px 10px",
              width: 250,
              borderRadius: 4,
              border: "1px solid #888",
              backgroundColor: "#222",
              color: "#fff"
            }}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                fetchData(1, statusFilter, searchQuery);
              }
            }}
          />
          <Button
            variant="outlined"
            onClick={() => fetchData(1, statusFilter, searchQuery)}
            size="small"
            sx={{ ml: 1 }}
          >
            🔍 Search
          </Button>
        </Box>

        <Box>
          <label style={{ marginRight: 8 }}>Filter by status:</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              fetchData(1, e.target.value, searchQuery);
            }}
            style={{
              padding: "6px 10px",
              backgroundColor: "#222",
              color: "#fff",
              border: "1px solid #888",
              borderRadius: "4px"
            }}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </Box>
      </Box>

        <Box sx={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", color: "white" }}>
            <thead>
              <tr>
                {[
                  { label: "Date", field: "created_at" },
                  { label: "User", field: "user_full_name" },
                  { label: "Email", field: "user_email" },
                  { label: "Product", field: "product_name" },
                  { label: "Final USD", field: "final_price_usd" },
                  { label: "Sessions", field: "sessions" },
                  { label: "Discount Code", field: "discount_code" },
                  { label: "Refunded", field: "refunded" },
                  { label: "Status", field: "status" }
                ].map(({ label, field }) => (
                  <th
                    key={field}
                    onClick={() => handleSort(field)}
                    style={{
                      cursor: "pointer",
                      padding: "8px",
                      backgroundColor: "#34163A"
                    }}
                  >
                    {label} {sortBy === field && (sortDir === "asc" ? "▲" : "▼")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedPurchases.map((p, idx) => (
                <tr key={p.id} style={{ backgroundColor: idx % 2 === 0 ? "#5E2F6E" : "transparent" }}>
                  <td style={{ padding: "8px", textAlign:"center"  }}>{new Date(p.created_at).toLocaleString()}</td>
                  <td style={{ padding: "8px", textAlign:"center"  }}>{p.user_full_name}</td>
                  <td style={{ padding: "8px", textAlign:"center"  }}>{p.user_email}</td>
                  <td style={{ padding: "8px", textAlign:"center"  }}>{p.product_name}</td>
                  <td style={{ padding: "8px", textAlign:"center"  }}>${p.final_price_usd}</td>
                  <td style={{ padding: "8px", textAlign:"center"  }}>{p.sessions}</td>
                  <td>{p.discount_code || "-"}</td>
                  <td style={{ padding: "8px", textAlign:"center"  }}>{p.refunded ? "Yes" : "No"}</td>
                  <td style={{ padding: "8px", textAlign:"center"  }}>
                    <select
                      value={p.payment_status}
                      disabled={p.payment_status === "paid"}
                      onChange={async (e) => {
                        const newStatus = e.target.value;
                        await api.patch(`/api/admin/purchases/${p.id}`, { payment_status: newStatus });
                        fetchData(page, statusFilter);
                      }}
                      style={{
                        backgroundColor:
                          p.payment_status === "paid" ? "#1b5e20" :
                          p.payment_status === "pending" ? "#b71c1c" :
                          "#888",
                        color: "#fff",
                        border: "1px solid #888",
                        borderRadius: "4px",
                        padding: "4px 8px",
                        cursor: p.payment_status === "paid" ? "default" : "pointer"
                      }}
                    >
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="failed">Failed</option>
                      <option value="refunded">Refunded</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                </tr>
              ))}
              {sortedPurchases.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: "16px", textAlign: "center", backgroundColor: "#452654" }}>
                    No data found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="outlined" disabled={page === 1} onClick={() => fetchData(page - 1, statusFilter)}>⬅ Prev</Button>
          <Button variant="outlined" disabled={page === totalPages || purchases.length === 0} onClick={() => fetchData(page + 1, statusFilter)}>Next ➡</Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminPurchases;
